import React from "react";
import {
  AssetContainsDrugUse,
  AssetContainsViolence,
  AssetForAllAgesFi,
  AssetForAllAgesSv,
  AssetMayCauseAnxiety,
  AssetNotForPersonsUnder7,
  AssetNotForPersonsUnder12,
  AssetNotForPersonsUnder16,
  AssetNotForPersonsUnder18,
  AssetSexualContent,
  SvgProps
} from "@yleisradio/yds-icons-react";
import styled from "styled-components";

import { useEmbedParametersContext } from "../contexts/embedParametersContext";
import { useMediaContext } from "../contexts/MediaContext";
import i18n from "../service/i18n";
import { Language } from "../types/common";
import { AgeRestriction, Reason } from "../types/player-api";

const Restrictions = styled.div`
  display: block;
  position: absolute;
  right: 10px;
  top: 10px;

  & > * {
    float: left;
    margin-left: 5px;
  }
`;

const AgeRestrictionIcon = ({
  ageRestriction,
  language,
  ...props
}: {
  ageRestriction: AgeRestriction;
  language: Language;
} & SvgProps) => {
  const ariaLabel = i18n(`content_rating_age_restriction_${ageRestriction}`, {
    language
  });

  const iconProps = {
    ...props,
    "aria-label": ariaLabel
  };

  switch (ageRestriction) {
    case 0:
      return language === "swe" ? (
        <AssetForAllAgesSv {...iconProps} />
      ) : (
        <AssetForAllAgesFi {...iconProps} />
      );
    case 7:
      return <AssetNotForPersonsUnder7 {...iconProps} />;
    case 12:
      return <AssetNotForPersonsUnder12 {...iconProps} />;
    case 16:
      return <AssetNotForPersonsUnder16 {...iconProps} />;
    case 18:
      return <AssetNotForPersonsUnder18 {...iconProps} />;
  }
};

const ReasonIcon = ({
  language,
  reason,
  ...props
}: {
  language: Language;
  reason: Reason;
} & SvgProps) => {
  switch (reason) {
    case "anxiety":
      return (
        <AssetMayCauseAnxiety
          aria-label={i18n("content_rating_anxiety", { language })}
          {...props}
        />
      );
    case "drugs-intoxicants":
      return (
        <AssetContainsDrugUse
          aria-label={i18n("content_rating_drugs_intoxicants", { language })}
          {...props}
        />
      );
    case "sex":
      return (
        <AssetSexualContent
          aria-label={i18n("content_rating_sex", { language })}
          {...props}
        />
      );
    case "violence":
      return (
        <AssetContainsViolence
          aria-label={i18n("content_rating_violence", { language })}
          {...props}
        />
      );
  }
};

const ContentRatingIcons = () => {
  const {
    contentRating: { age_restriction, reasons }
  } = useMediaContext();
  const { language } = useEmbedParametersContext();

  const iconProps = {
    "aria-hidden": false,
    height: 40,
    role: "img",
    width: 40
  };

  return (
    <Restrictions>
      {age_restriction !== undefined && age_restriction !== null && (
        <AgeRestrictionIcon
          ageRestriction={age_restriction}
          language={language}
          {...iconProps}
        />
      )}
      {reasons &&
        reasons.map(reason => (
          <ReasonIcon
            key={reason}
            language={language}
            reason={reason}
            {...iconProps}
          />
        ))}
    </Restrictions>
  );
};

export default ContentRatingIcons;
